/**
 * 문서 목록 상태 배지 — 목업 `.status`.
 *
 * `pages/reports/index.tsx` 안의 지역 컴포넌트였는데, 결재 진행 substate(S39)를 얹으면서
 * 별도 모듈로 뺐다. 목록 페이지 전체를 부팅하지 않고 배지만 렌더해 검증하기 위해서다
 * (페이지는 react-query·router·store를 물고 있어 배지 문구 하나를 확인하는 값으로는 비싸다).
 */
import { Badge } from '@safetyhub/ui';
import { getDocStatusBadgeTone, resolveApprovalProgressLabel, type Report } from '../../lib/reports';
import { resolveDelegationProgressLabel } from '../../lib/delegationProgressLabel';
import { resolveReportStatusLabel } from '../../lib/reportStatusLabel';

/**
 * **결재 대기만 점이 숨쉰다**(`.status.s-pending .dot`의 breathe 애니메이션). 기다리는 중이라는
 * 게 목록에서 눈에 띄어야 하는 유일한 상태다.
 *
 * 그 한 상태만 `dot` prop 대신 children으로 점을 넣는 이유: 프리미티브의 점은 클래스를 받지
 * 않아(Badge가 색·크기를 소유) 애니메이션을 붙일 자리가 없다. 결재함의 "대기" 태그가 같은
 * 방식이다(`pages/approvals/index.tsx`). 점 색을 리터럴로 적는 것도 그래서인데,
 * 이 분기는 tone이 항상 amber(`DOC_STATUS_TONE.PENDING_APPROVAL`)라 갈릴 여지가 없다.
 */
export function DocStatusBadge({
  report,
  className,
}: {
  /**
   * 상태 enum이 아니라 report 행을 받는다 — 결재 정책이 없는 완료본('완료')과 결재를 거친
   * 완료본('결재완료')은 status가 둘 다 APPROVED라 enum만으로는 구별되지 않는다.
   * 목록 응답도 `approvalSignatures`를 실으므로 상세와 같은 축으로 판정할 수 있다.
   */
  report: Report;
  className?: string;
}) {
  const status = report.status;
  const label = resolveReportStatusLabel(report);
  const tone = getDocStatusBadgeTone(status);

  // 배지 아래 한 줄에는 **한 축만** 붙는다(S39 축 확정, 2026-08-30):
  //  - 결재 대기: 결재자 축 — '안전관리자 발송됨'처럼 지금 누가 들고 있는지(S39).
  //  - 그 앞(초안·반려): 위임 담당자 축 — '박담당 작성 중'(S39-c). 위임이 아니면 null이라
  //    종전 그대로 상태 라벨만 보인다.
  // 두 축의 상태 구간이 겹치지 않으므로 이 분기가 곧 배타다(서버 응답도 같은 게이트를 쓴다).
  const progressLabel =
    status === 'PENDING_APPROVAL'
      ? resolveApprovalProgressLabel(report)
      : resolveDelegationProgressLabel(report);

  const badge =
    status === 'PENDING_APPROVAL' ? (
      <Badge tone={tone} className={progressLabel ? undefined : className}>
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 flex-none rounded-full bg-amber-600 motion-safe:animate-breathe"
        />
        {label}
      </Badge>
    ) : (
      <Badge tone={tone} dot className={progressLabel ? undefined : className}>
        {label}
      </Badge>
    );

  if (!progressLabel) return badge;

  // 배지 안에 이어 붙이지 않고 아래 줄로 내린다 — 표 셀이 `whitespace-nowrap`이라
  // "결재 대기 · 현장소장 열람됨 (2차)"를 한 줄로 넣으면 상태 열이 그만큼 넓어진다.
  // 단계명이 길 수 있어(직함 그대로 쓴다) 폭을 묶고 넘치면 잘라 title로 전문을 남긴다.
  return (
    <div className={className}>
      {badge}
      <div
        className="mt-0.5 max-w-[160px] truncate text-xs font-medium text-ink-500"
        title={progressLabel}
      >
        {progressLabel}
      </div>
    </div>
  );
}

export default DocStatusBadge;
